import { Component, signal, WritableSignal } from '@angular/core';
import { CaseSheetService } from '../services/case-sheet.service';
import { CaseSheetParams, CaseSheetResponse } from '../models/case-sheet.model';
import { IBreadcrumb } from '@lib/models/breadcrumb.model';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { CommonModule, formatDate } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TKeyValue } from '@app/lib/models/common.type';
import { ECaseCategory, ECourtId } from '@app/dashboard/models/constant';
import { MatIconModule } from '@angular/material/icon';
import { DialogMessageComponent } from '../dialog-message/dialog-message.component';
import { MatDialog } from '@angular/material/dialog';
import { IAttachmentDialogData } from '@app/lib/models/dialog-data.model';
import { AttachmentViewerComponent } from '@app/lib/components/attachment-viewer/attachment-viewer.component';
import { DialogService } from '@app/lib/services/dialog.service';
import { ILoginData } from '@app/lib/models/login.model';
import { AdditionalSupremeCourtCaseData, CaseDataPayload, DocParty, DocPartyData, DocType, IDRattachementParams, IDRAttachment, IDRCaseCommentsDTO, IDRCaseDocument, IDRCaseSearchRequestBody, IDRlistOfFiles, IDRlistOfFilesParams, IDRmotionCaseNumber, IDRmotionCaseNumberParams, IDRPartiesDTO, IDRPartyRepresentationDTO, Label, PrintRecieptRequest, WitnessData } from '../models/document-reception.model';

@Component({
  selector: 'app-case-sheet',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    BreadcrumbComponent,
    MatIconModule,
    ScrollingModule
  ],
  templateUrl: './case-sheet.component.html',
  styleUrls: ['./case-sheet.component.scss'],
})
export class CaseSheetComponent {
  public caseCategoryEnum = ECaseCategory;
  userType: string = '';
  courtData: ILoginData | null = null;
  today: Date = new Date();
  displayedColumns: string[] = [];
  columnDefinitions: any[] = [];
  filteredDocumentTypes: DocType[] = [];
  selectedDocumentType: DocType | null = null;
  documentSearch: string = '';
  dateReceived: Date | null = null;
  isUploading: boolean = false;
  courtIdData: number | null = null;
  isSupremeCourtFlg: boolean = false;
  isCOAFlg: boolean = false;

  public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([]);

  get isAppeal(): boolean {
    return (
      this.courtIdData === ECourtId.SC_ID ||
      this.courtIdData === ECourtId.COA_ID
    );
  }

  setTableHeaders(): void {
    this.
      columnDefinitions = [
        {
          column: 'caseId',
          labels: { lawyer: 'Case ID', admin: 'Appeal ID' },
          show: true,
        },
        {
          column: 'caseNumber',
          labels: { lawyer: 'Case Number', admin: 'Appeal Number' },
          show: true
        },
        {
          column: 'appellantsParty',
          labels: { lawyer: 'Plaintiff(s)/Claimant(s)', admin: 'Appellant(s)' },
          show: true
        },
        {
          column: 'respondentsParty',
          labels: { lawyer: 'Defendant(s)', admin: 'Respondent(s)' },
          show: true
        },
        {
          column: 'documentStatus',
          labels: { lawyer: 'Document Status', admin: 'Document Status' },
          show: true
        }
      ];

    this.displayedColumns = this.columnDefinitions
      .filter(col => col.show)
      .map(col => col.column);
  }
  caseList: any[] = [];
  isLoading = false;
  totalCount: number = 0;
  // today=new Date;

  public caseCategories: TKeyValue[] = [
    { key: this.caseCategoryEnum.FI, value: 'First Instance' },
    { key: this.caseCategoryEnum.A, value: 'Appeal' },
  ];

  public searchForm: FormGroup = new FormGroup({
    fromDate: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    toDate: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    caseCategory: new FormControl(),
  });

  constructor(private caseSheetService: CaseSheetService,
    private dialog: MatDialog,
    private dialogService: DialogService
  ) { }
  ngOnInit(): void {
    this.loadCourtData();
    this.setTableHeaders();
  }

  getCourtNameData(): ILoginData | null {
    try {
      const userData = localStorage.getItem('userData') as string;
      const userData2 = userData ? JSON.parse(userData) : null;
      this.courtIdData = userData2.courtId;
      if (this.courtIdData && this.courtIdData === ECourtId.SC_ID) {

        this.isSupremeCourtFlg = true;
      } else if (this.courtIdData && this.courtIdData === ECourtId.COA_ID) {
        this.isCOAFlg = true;
      }
      return userData ? JSON.parse(userData) : null;
    } catch (err) {
      return null
    }

  }
  loadCourtData(): void {
    this.courtData = this.getCourtNameData();

    this.userType =
      this.courtData?.courtId === 1 || this.courtData?.courtId === 2
        ? 'admin'
        : 'lawyer';
      
        const caseCategoryControl = this.searchForm.get('caseCategory');

if (!this.isAppeal) {
  // Case Sheet → mandatory
  caseCategoryControl?.setValidators([Validators.required]);
} else {
  // Appeal Sheet → NOT mandatory
  caseCategoryControl?.clearValidators();
}

caseCategoryControl?.updateValueAndValidity(); 
    this.breadcrumbs.set([
      { label: 'File Management' },
      { label: this.isAppeal ? 'Appeal Sheet' : 'Case Sheet' },
    ]);
  }

  onSearch() {
    if (this.searchForm.invalid) {
      this.dialogService.openDialog1({
        data: {
          title: "Infomation",
          message: "Please fill all the search criteria",
          icon: 'info', // ✅ Default icon if not provided
        }, disableClose: true,           // Prevents closing on outside click or ESC
        hasBackdrop: true,            // Shows backdrop to block interaction
        backdropClass: 'dialog-backdrop', // Optional: custom styling
      });

      return;
    }

    const formattedFromDate = formatDate(this.searchForm.value.fromDate, 'dd-MMM-yyyy', 'en-US');
    const formattedToDate = formatDate(this.searchForm.value.toDate, 'dd-MMM-yyyy', 'en-US');
    const caseCategory = this.searchForm.value.caseCategory;

    console.log('Formatted From Date:', formattedFromDate);
    console.log('Formatted To Date:', formattedToDate);
    console.log('Case Category:', caseCategory);

    const request = new CaseSheetParams(formattedFromDate, formattedToDate, caseCategory);

    this.isLoading = true;
    this.caseSheetService.getCaseSheetList(request).subscribe({
      next: (response: CaseSheetResponse) => {
        this.caseList = response.data;
        this.totalCount = response.totalCount;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching case sheet list:', err);
        this.isLoading = false;
      },
    });
  }

  onReset() {
    this.searchForm.reset();
    this.caseList = [];
    this.totalCount = 0;
  }
  onPrint(): void {
    const fromDate = formatDate(this.searchForm.value.fromDate, 'dd-MMM-yyyy', 'en-US');
    const toDate = formatDate(this.searchForm.value.toDate, 'dd-MMM-yyyy', 'en-US');
    const caseCategory = this.searchForm.value.caseCategory;
    const data: IAttachmentDialogData = {
      path: 'appeal-sheet/print-receipt.htm',
      queryParams: {
        fromDate: fromDate,
        toDate: toDate,
        caseCategory: caseCategory
      }
    };

    this.dialog.open(AttachmentViewerComponent, {
      panelClass: 'dialog-pdf-viewer',
      data,
      disableClose: true,           // Prevents closing on outside click or ESC
      hasBackdrop: true,            // Shows backdrop to block interaction
      backdropClass: 'dialog-backdrop', // Optional: custom styling
    });
  }


}